import { useEffect, useState } from "react";
import { DataTable } from "mantine-datatable";
import { ICurrencyRates, IDonation } from "../types";
import { Avatar, Group, Text } from "@mantine/core";
import { makeGetRequest } from "../services/httpHelper";
import { convertCurrency, isErrorObject } from "../utils/utils";
import ErrorHandler from "../services/ErrorHandler";

const PAGE_SIZE = 10;

interface DonationsTableProps {
  donations: IDonation[];
  campaign_currency: string;
}

const CampaignDonorsTable: React.FC<DonationsTableProps> = ({ donations, campaign_currency }) => {
  const [page, setPage] = useState(1);
  const [records, setRecords] = useState(donations.slice(0, PAGE_SIZE));
  const [userCurrencyCode, setUserCurrencyCode] = useState("USD");
  const [exchangeRates, setExchangeRates] = useState<ICurrencyRates>();

  useEffect(() => {
    // Fetch exchange rates
    const fetchExchangeRates = async () => {
      const endpoint = "latest-currency-exchange-rates";
      const res = await makeGetRequest(endpoint);
      if (isErrorObject(res)) {
        const errorMessage = ErrorHandler(res);
        console.log(errorMessage);
      } else {
        setExchangeRates(res.data);
      }
    };

    // Get user's location and currency
    const fetchUserLocation = async () => {
      try {
        const response = await fetch("https://ipapi.co/json/");
        const locationData = await response.json();
        const userCurrency = locationData.currency || "USD";
        setUserCurrencyCode(userCurrency.toUpperCase());
      } catch (err) {
        console.error("Failed to get user location:", err);
        setUserCurrencyCode("USD");
      }
    };

    fetchExchangeRates();
    fetchUserLocation();
  }, []);

  useEffect(() => {
    const from = (page - 1) * PAGE_SIZE;
    const to = from + PAGE_SIZE;
    donations && setRecords(donations.slice(from, to));
  }, [page, donations]);

  // Currency conversion handler
  const handleCurrencyConversion = (amount: number, fromCurrency: string, toCurrency: string) => {
    return convertCurrency(amount, fromCurrency, toCurrency, exchangeRates?.rates || {});
  };

  const formatDonationAmount = (amount: number, donationCurrency: string) => {
    if (userCurrencyCode === campaign_currency) {
      if (donationCurrency !== campaign_currency) {
        const convertedAmount = handleCurrencyConversion(amount, donationCurrency, campaign_currency);
        return `${campaign_currency} ${convertedAmount?.amount.toFixed(2)} (${donationCurrency} ${amount.toFixed(2)})`;
      }
      return `${campaign_currency} ${amount.toFixed(2)}`;
    } else {
      const convertedAmount = handleCurrencyConversion(amount, donationCurrency, "USD");
      return `USD ${convertedAmount?.amount.toFixed(2)} (${donationCurrency} ${amount.toFixed(2)})`;
    }
  };

  return (
    <DataTable
      columns={[
        {
          accessor: "Donor",
          render: ({ first_name, last_name, donor_anonymous }: IDonation) => (
            <Group>
              {donor_anonymous ? (
                <>
                  <Avatar src="https://www.freepik.com/vectors/profile-placeholder" alt="anonymous" size="sm" radius="xl" />
                  <Text>Anonymous</Text>
                </>
              ) : (
                <>
                  <Avatar src="https://www.freepik.com/vectors/profile-placeholder" alt="profile photo" size="sm" radius="xl" />
                  <Text>
                    {first_name} {last_name}
                  </Text>
                </>
              )}
            </Group>
          ),
        },
        { accessor: "donation_currency" },
        {
          accessor: "donation_amount",
          render: ({ donation_amount, donation_currency }: IDonation) => (
            <Text>{formatDonationAmount(parseFloat(donation_amount), donation_currency)}</Text>
          ),
        },
      ]}
      records={records}
      totalRecords={donations.length}
      recordsPerPage={PAGE_SIZE}
      page={page}
      onPageChange={setPage}
      highlightOnHover
      verticalSpacing="sm"
      striped
    />
  );
};

export default CampaignDonorsTable;
